// ===================================================================
// 🎫 ANÁLISE DE PEDIDOS DE ISENÇÃO - MÓDULO ANALISTA
// ===================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAdminProcess } from '../../contexts/AdminProcessContext';
import { parseAdminSelectedProcessId } from '../../utils/adminProcessId';
import {
  initialAdminTableSort,
  nextAdminTableSort,
  sortColumnIndicator,
  sortExemptionRequestsForDisplay,
} from '../../utils/adminTableSort';
import { getButtonStyle, ACTION_ICONS } from '../../constants/statusTranslations';

// Sistema simplificado - apenas decisão e motivos

export const ExemptionAnalysis = React.memo(({ user }) => {
  const { selectedProcessId, setSelectedProcessId, processes } = useAdminProcess();
  const [exemptionRequests, setExemptionRequests] = useState([]);
  const [courses, setCourses] = useState([]);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Estados do modal de análise
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [requestToReturn, setRequestToReturn] = useState(null);
  const [cadunicoConsulting, setCadunicoConsulting] = useState(false);
  const [analysisForm, setAnalysisForm] = useState({
    decision: '',
    rejection_reason: '',
    notes: ''
  });
  
  // Estado de filtro de status
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [filterCampus, setFilterCampus] = useState('');
  const [filterQuota, setFilterQuota] = useState('');
  const [tableSort, setTableSort] = useState(() => initialAdminTableSort());

  const { addNotification } = useNotifications();

  const loadData = useCallback(async (isBackground = false) => {
    const { id: processId } = parseAdminSelectedProcessId(selectedProcessId);
    if (processId == null) {
      setExemptionRequests([]);
      if (!isBackground) setLoading(false);
      return;
    }
    try {
      if (!isBackground) setLoading(true);
      const [allExemptionsData, coursesData, rejectionReasonsData] = await Promise.all([
        apiClient.getExemptionRequests({ processId }),
        apiClient.getCourses(processId),
        apiClient.getRejectionReasons({ context: 'exemption' })
      ]);
      const exemptionsData = Array.isArray(allExemptionsData) ? allExemptionsData : [];
      setExemptionRequests(exemptionsData);
      setCourses(Array.isArray(coursesData) ? coursesData : []);
      setRejectionReasons(Array.isArray(rejectionReasonsData) ? rejectionReasonsData : []);
    } catch (err) {
      if (!isBackground) setError('Erro ao carregar pedidos de isenção');
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [user?.id, selectedProcessId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (showAnalysisModal || showReturnModal) return;
    const interval = setInterval(() => loadData(true), 60 * 1000);
    return () => clearInterval(interval);
  }, [loadData, showAnalysisModal, showReturnModal]);

  // Filtrar pedidos (pending inclui 'pending' e 'analyzing')
  const filteredRequests = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    return exemptionRequests.filter((request) => {
      if (procSel.id != null && request.process_id !== procSel.id) return false;
      if (filterStatus) {
        if (filterStatus === 'pending' && (request.status === 'pending' || request.status === 'analyzing')) return true;
        if (request.status !== filterStatus) return false;
      }
      if (filterCourse && String(request.course_name || '') !== String(filterCourse)) return false;
      if (filterCampus && String(request.course_campus || '') !== String(filterCampus)) return false;
      if (filterQuota && String(request.quota_type || 'AC') !== String(filterQuota)) return false;
      return true;
    });
  }, [exemptionRequests, filterStatus, filterCourse, filterCampus, filterQuota, selectedProcessId]);

  const sortedRequests = useMemo(
    () => sortExemptionRequestsForDisplay(filteredRequests, tableSort),
    [filteredRequests, tableSort]
  );

  useEffect(() => {
    setTableSort(initialAdminTableSort());
  }, [selectedProcessId]);

  const courseFilterOptions = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    const names = new Set();
    exemptionRequests.forEach((request) => {
      if (procSel.id != null && request.process_id !== procSel.id) return;
      if (request.course_name) names.add(String(request.course_name));
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [exemptionRequests, selectedProcessId]);

  const campusFilterOptions = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    const campuses = new Set();
    exemptionRequests.forEach((request) => {
      if (procSel.id != null && request.process_id !== procSel.id) return;
      if (request.course_campus) campuses.add(String(request.course_campus));
    });
    return Array.from(campuses).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [exemptionRequests, selectedProcessId]);

  const quotaFilterOptions = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    const quotas = new Set();
    exemptionRequests.forEach((request) => {
      if (procSel.id != null && request.process_id !== procSel.id) return;
      quotas.add(String(request.quota_type || 'AC'));
    });
    return Array.from(quotas).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [exemptionRequests, selectedProcessId]);

  // Estatísticas (pending = atribuído, analyzing = em análise)
  const stats = useMemo(() => {
    const total = filteredRequests.length;
    const pending = filteredRequests.filter(r => r.status === 'pending' || r.status === 'analyzing').length;
    const approved = filteredRequests.filter(r => r.status === 'approved').length;
    const rejected = filteredRequests.filter(r => r.status === 'rejected').length;
    
    return { total, pending, approved, rejected };
  }, [filteredRequests]);

  // Aceitar tarefa: muda de Isenção Atribuída para Isenção em Análise
  const handleAcceptTask = useCallback(async (request) => {
    try {
      await apiClient.updateExemptionRequest(request.id, { status: 'analyzing' });
      setExemptionRequests(prev => prev.map(r => r.id === request.id ? { ...r, status: 'analyzing' } : r));
      addNotification('Sucesso', 'Tarefa aceita. Situação: Isenção em Análise', 'success');
    } catch (e) {
      addNotification('Erro', 'Erro ao aceitar tarefa', 'error');
    }
  }, [addNotification]);


  const handleConsultCadunico = useCallback(async () => {
    if (!selectedRequest?.application_id) return;
    setCadunicoConsulting(true);
    try {
      const result = await apiClient.consultCadunico(selectedRequest.application_id, {
        nis: selectedRequest.exemption_nis,
      });
      setSelectedRequest(prev => prev ? {
        ...prev,
        exemption_cadunico_result: result,
        exemption_cadunico_consulted_at: result?.consulted_at || new Date().toISOString(),
      } : prev);
      setExemptionRequests(prev => prev.map(r => (
        r.application_id === selectedRequest.application_id
          ? { ...r, exemption_cadunico_result: result, exemption_cadunico_consulted_at: result?.consulted_at }
          : r
      )));
      addNotification('Sucesso', result?.message || 'Consulta CadÚnico registrada. Deferimento continua manual.', result?.ok === false ? 'warning' : 'success');
    } catch (err) {
      addNotification('Erro', err.message || 'Falha ao consultar CadÚnico', 'error');
    } finally {
      setCadunicoConsulting(false);
    }
  }, [selectedRequest, addNotification]);

  // Abrir modal de devolução
  const openReturnModal = useCallback((request) => {
    setRequestToReturn(request);
    setReturnReason('');
    setShowReturnModal(true);
  }, []);

  // Devolver tarefa: remove atribuição e volta para a fila do admin (com motivo)
  const handleReturnTask = useCallback(async () => {
    if (!requestToReturn) return;
    try {
      await apiClient.updateApplication(requestToReturn.application_id, {
        analyst_id: null,
        exemption_return_reason: returnReason.trim() || null
      });
      addNotification('Sucesso', 'Tarefa devolvida com sucesso', 'success');
      setShowReturnModal(false);
      setRequestToReturn(null);
      setReturnReason('');
      loadData();
    } catch (e) {
      addNotification('Erro', 'Erro ao devolver tarefa', 'error');
    }
  }, [requestToReturn, returnReason, addNotification, loadData]);

  // Abrir formulário de análise (apenas se status = analyzing, approved ou rejected)
  const handleAnalyze = useCallback(async (request) => {
    if (request.status === 'pending') return; // Analisar inativo até aceitar
    setSelectedRequest(request);
    
    if (request.status !== 'pending') {
      let rejectionReason = request.exemption_reason || request.rejection_reason || '';
      // Se for ID numérico (dado antigo), resolver para o título
      if (/^\d+$/.test(String(rejectionReason).trim())) {
        const reason = rejectionReasons.find(r => r.id == rejectionReason);
        if (reason) rejectionReason = reason.title;
      }
      const st = (request.status || '').toLowerCase();
      const decision =
        st === 'approved' ? 'approved' : st === 'rejected' ? 'rejected' : 'approved';
      setAnalysisForm({
        decision,
        rejection_reason: rejectionReason,
        notes: request.notes || ''
      });
    } else {
      setAnalysisForm({
        decision: '',
        rejection_reason: '',
        notes: ''
      });
    }
    
    setShowAnalysisModal(true);
  }, [rejectionReasons]);

  // Salvar análise
  const saveExemptionAnalysis = useCallback(async () => {
    if (!selectedRequest) return;

    // Validações
    if (analysisForm.decision === 'rejected' && !analysisForm.rejection_reason) {
      alert('É obrigatório selecionar um motivo de indeferimento');
      return;
    }

    if (analysisForm.decision !== 'approved' && analysisForm.decision !== 'rejected') {
      alert('Selecione Deferido ou Indeferido.');
      return;
    }

    try {
      const { error: exError } = await apiClient.updateExemptionRequest(selectedRequest.id, {
        status: analysisForm.decision
      });
      if (exError) throw new Error(exError);

      const appUpdate = analysisForm.decision === 'approved'
        ? { status: 'exemption_approved', exemption_status: 'approved', exemption_reason: null }
        : { status: 'exemption_rejected', exemption_status: 'rejected', exemption_reason: analysisForm.rejection_reason || null };
      await apiClient.updateApplication(selectedRequest.application_id, appUpdate);

      addNotification(
        'Sucesso', 
        `Análise de isenção ${analysisForm.decision === 'approved' ? 'aprovada' : 'rejeitada'} com sucesso`,
        'success'
      );
      
      setShowAnalysisModal(false);
      
      // Forçar atualização local do estado antes de recarregar
      setExemptionRequests(prevRequests => 
        prevRequests.map(req => 
          req.id === selectedRequest.id 
            ? { ...req, status: analysisForm.decision, analyzed_at: new Date().toISOString() }
            : req
        )
      );
      
      // Aguardar um momento antes de recarregar dados
      setTimeout(() => {
        loadData(); // Recarregar dados
      }, 100);
    } catch (error) {
      console.error('Erro ao salvar análise:', error);
      addNotification('Erro', 'Erro ao salvar análise de isenção', 'error');
    }
  }, [selectedRequest, analysisForm, user.id, addNotification, loadData]);

  // Download de um documento específico via API (garante funcionamento)
  const handleDownloadDocument = useCallback(async (request, doc, index) => {
    const filename = doc.name || `documento_${index + 1}.pdf`;
    try {
      const blob = await apiClient.downloadDocument({ exemptionRequestId: request.id, index });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      addNotification('Sucesso', `${filename} baixado`, 'success');
    } catch (e) {
      addNotification('Erro', e.message || `Não foi possível baixar ${filename}`, 'error');
    }
  }, [addNotification]);

  // Download de todos os documentos
  const handleDownloadDocuments = useCallback((request) => {
    const documents = request.documents || [];
    if (documents.length === 0) {
      addNotification('Info', 'Nenhum documento anexado a este pedido', 'info');
      return;
    }
    documents.forEach((doc, index) => handleDownloadDocument(request, doc, index));
  }, [addNotification, handleDownloadDocument]);

  // Função para verificar se está dentro do prazo de isenção
  const isWithinExemptionPeriod = useCallback((processId) => {
    const process = processes.find(p => p.id === processId);
    if (!process) return false;
    
    const now = new Date();
    const exemptionStart = new Date(process.exemption_start_date);
    const exemptionEnd = new Date(process.exemption_end_date);
    
    return now >= exemptionStart && now <= exemptionEnd;
  }, [processes]);

  // Função para verificar se está dentro do prazo de recurso
  const isWithinAppealPeriod = useCallback((processId) => {
    const process = processes.find(p => p.id === processId);
    if (!process) return false;
    
    const now = new Date();
    const appealStart = new Date(process.appeal_start_date);
    const appealEnd = new Date(process.appeal_end_date);
    
    return now >= appealStart && now <= appealEnd;
  }, [processes]);

  // Função para enviar novo documento
  const handleSendNewDocument = useCallback(async (request) => {
    try {
      // Voltar o status para pending para permitir nova análise
      await apiClient.updateExemptionRequest(request.id, { status: 'pending' });
      await apiClient.updateApplication(request.application_id, {
        status: 'exemption_pending_assignment',
        exemption_status: 'pending'
      });

      addNotification('Sucesso', 'Status alterado para permitir envio de novo documento', 'success');
      loadData();
    } catch (error) {
      console.error('Erro ao permitir novo documento:', error);
      addNotification('Erro', 'Erro ao alterar status', 'error');
    }
  }, [addNotification, loadData]);

  // Função para interpor recurso
  const handleAppeal = useCallback(async (request) => {
    try {
      // Criar registro de recurso
      const appeal = {
        id: Date.now(),
        application_id: request.application_id,
        user_id: request.user_id,
        process_id: request.process_id,
        course_id: request.course_id,
        candidate_name: request.candidate_name,
        candidate_cpf: request.candidate_cpf,
        candidate_email: request.candidate_email,
        status: 'pending_assignment',
        reason: 'Recurso contra indeferimento de isenção',
        documents: request.documents || [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      await apiClient.createAppeal({
        application_id: request.application_id,
        justification: appeal.reason,
        documents: appeal.documents || []
      });
      await apiClient.updateApplication(request.application_id, {
        status: 'appeal_pending_assignment'
      });

      addNotification('Sucesso', 'Recurso interposto com sucesso', 'success');
      loadData();
    } catch (error) {
      console.error('Erro ao interpor recurso:', error);
      addNotification('Erro', 'Erro ao interpor recurso', 'error');
    }
  }, [addNotification, loadData]);

  // Função para obter estilo do status
  const getStatusStyle = useCallback((status) => {
    const baseStyle = {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '9px',
      fontWeight: '500'
    };
    
    switch (status) {
      case 'approved':
        return { ...baseStyle, backgroundColor: '#D1FAE5', color: '#065F46' };
      case 'rejected':
        return { ...baseStyle, backgroundColor: '#FEE2E2', color: '#991B1B' };
      case 'pending':
      case 'analyzing':
        return { ...baseStyle, backgroundColor: '#FEF3C7', color: '#92400E' };
      default:
        return { ...baseStyle, backgroundColor: '#F3F4F6', color: '#374151' };
    }
  }, []);

  if (loading && !showAnalysisModal && !showReturnModal) {
    return <LoadingSpinner />;
  }

  return (
    <div style={styles.contentContainer}>
      <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', marginBottom: '24px' }}>
        🎫 Análise de Pedidos de Isenção
      </h2>

      {error && (
        <Alert type="error" message={error} onClose={() => setError('')} />
      )}

      {/* Resumo */}
      <div style={{ ...styles.card, ...styles.mb6 }}>
        <div style={{ fontSize: '14px', color: '#374151' }}>
          <strong>Pedidos:</strong> {stats.total} total •
          <span style={{ color: '#D97706', margin: '0 8px' }}>⏳ {stats.pending} pendentes</span> •
          <span style={{ color: '#059669', margin: '0 8px' }}>✅ {stats.approved} aprovadas</span> •
          <span style={{ color: '#DC2626' }}>❌ {stats.rejected} rejeitadas</span>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ ...styles.card, ...styles.mb6 }}>
        <h3 style={styles.cardTitle}>🔍 Filtros</h3>
        <div style={{ ...styles.flex, ...styles.gap4, ...styles.flexWrap }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Processo Seletivo</label>
            <select
              value={selectedProcessId}
              onChange={setSelectedProcessId}
              style={styles.select}
            >
              <option value="">Selecione um processo</option>
              {processes.map(process => (
                <option key={process.id} value={process.id}>
                  {process.name || process.title}
                </option>
              ))}
            </select>
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Situação</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={styles.select}
            >
              <option value="">Todos os status</option>
              <option value="pending">Pendente</option>
              <option value="approved">Aprovado</option>
              <option value="rejected">Rejeitado</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Curso</label>
            <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)} style={styles.select}>
              <option value="">Todos os cursos</option>
              {courseFilterOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Unidade</label>
            <select value={filterCampus} onChange={(e) => setFilterCampus(e.target.value)} style={styles.select}>
              <option value="">Todas as unidades</option>
              {campusFilterOptions.map((campus) => (
                <option key={campus} value={campus}>{campus}</option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Cota</label>
            <select value={filterQuota} onChange={(e) => setFilterQuota(e.target.value)} style={styles.select}>
              <option value="">Todas as cotas</option>
              {quotaFilterOptions.map((quota) => (
                <option key={quota} value={quota}>{quota}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Legenda de ícones */}
      <div style={{ ...styles.card, marginBottom: '12px', padding: '14px 20px', backgroundColor: '#F9FAFB' }}>
        <div style={{ fontSize: '15px', color: '#374151' }}>
          <strong>Legenda:</strong> {ACTION_ICONS.analyze} Analisar · {ACTION_ICONS.edit} Editar
        </div>
      </div>

      {/* Lista de Pedidos de Isenção */}
      <div style={styles.card}>
        <div style={{ ...styles.flexBetween, ...styles.mb4 }}>
          <h3 style={styles.cardTitle}>
            Pedidos de Isenção ({filteredRequests.length})
          </h3>
        </div>
        
        {filteredRequests.length === 0 ? (
          <div style={styles.emptyState}>
            <p>Nenhum pedido de isenção encontrado</p>
          </div>
        ) : (
          <div style={{ ...styles.tableContainer, ...styles.tableScrollContainer }}>
            <table style={{ ...styles.table, width: '100%', fontSize: '10px', hyphens: 'none' }}>
              <thead>
                <tr>
                  <th
                    role="columnheader"
                    aria-sort={tableSort.key === 'id' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                    style={{ ...styles.tableHeaderCell, fontSize: '9px', padding: '8px 6px', width: '45px', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setTableSort((p) => nextAdminTableSort(p, 'id'))}
                    title="Ordenar por número da inscrição"
                  >
                    Nº{sortColumnIndicator(tableSort, 'id')}
                  </th>
                  <th
                    role="columnheader"
                    aria-sort={tableSort.key === 'candidate' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                    style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setTableSort((p) => nextAdminTableSort(p, 'candidate'))}
                    title="Ordenar por nome do candidato"
                  >
                    Candidato{sortColumnIndicator(tableSort, 'candidate')}
                  </th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Curso</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Unidade</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Cota</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Situação</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Data Solicitação</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px', textAlign: 'center' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedRequests.map(request => {
                  const process = processes.find(p => p.id === request.process_id);
                  // exemption_reason = motivo de indeferimento; só indica "reenvio" quando o pedido
                  // voltou à fila (pending/analyzing) com motivo preservado — não quando status=rejected.
                  const showResubmittedBadge =
                    request.exemption_reason &&
                    (request.status === 'pending' || request.status === 'analyzing');
                  return (
                    <tr key={request.id} style={styles.tableRow}>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 6px', fontWeight: '600', color: '#374151' }}>
                        #{request.application_id}
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        <div style={{ fontWeight: '600', color: '#1F2937', fontSize: '10px' }}>
                          {request.candidate_name}
                        </div>
                        <div style={{ fontSize: '9px', color: '#6B7280' }}>
                          CPF: {request.candidate_cpf || 'N/A'}
                        </div>
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        <div>
                          <div style={{ fontWeight: '600', color: '#111827', fontSize: '10px' }}>
                            {request.course_name || 'N/A'}
                          </div>
                          {showResubmittedBadge && (
                            <span style={{
                              display: 'inline-block',
                              marginTop: '4px',
                              padding: '2px 8px',
                              fontSize: '11px',
                              backgroundColor: '#FEF3C7',
                              color: '#92400E',
                              borderRadius: '4px',
                              fontWeight: '500'
                            }} title={`Motivo do indeferimento anterior: ${request.exemption_reason}`}>
                              📄 Documento reenviado
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>{request.course_campus || '—'}</td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>{request.quota_type || 'AC'}</td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        <span style={getStatusStyle(request.status)}>
                          {request.status === 'pending' && '⏳ Isenção Atribuída'}
                          {request.status === 'analyzing' && '📝 Isenção em Análise'}
                          {request.status === 'approved' && '✅ Aprovado'}
                          {request.status === 'rejected' && '❌ Rejeitado'}
                        </span>
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        {new Date(request.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px', textAlign: 'center' }}>
                        <div style={{ ...styles.flex, ...styles.gap2, justifyContent: 'center', flexWrap: 'wrap' }}>
                          {request.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleAcceptTask(request)}
                                style={getButtonStyle('primary', 'small')}
                                title="Aceitar tarefa (muda para Isenção em Análise)"
                              >
                                ✅ Aceitar
                              </button>
                              <button
                                onClick={() => openReturnModal(request)}
                                style={getButtonStyle('secondary', 'small')}
                                title="Devolver tarefa para o admin"
                              >
                                ↩️ Devolver
                              </button>
                              <button
                                disabled
                                style={{ ...getButtonStyle('secondary', 'small'), opacity: 0.5, cursor: 'not-allowed' }}
                                title="Aceite a tarefa para analisar"
                              >
                                {ACTION_ICONS.analyze}
                              </button>
                            </>
                          )}
                          {request.status === 'analyzing' && (
                            <>
                              <button
                                onClick={() => openReturnModal(request)}
                                style={getButtonStyle('secondary', 'small')}
                                title="Devolver tarefa para o admin"
                              >
                                ↩️ Devolver
                              </button>
                              <button
                                onClick={() => handleAnalyze(request)}
                                style={getButtonStyle('primary', 'small')}
                                title="Abrir formulário de análise"
                              >
                                {ACTION_ICONS.analyze}
                              </button>
                            </>
                          )}
                          {(request.status === 'approved' || request.status === 'rejected') && (
                            <button
                              onClick={() => handleAnalyze(request)}
                              style={getButtonStyle('secondary', 'small')}
                              title="Editar análise"
                            >
                              {ACTION_ICONS.edit} Editar
                            </button>
                          )}
                          
                          {/* Botões para pedidos rejeitados */}
                          {request.status === 'rejected' && (
                            <div style={{ ...styles.flex, ...styles.gap2, marginTop: '4px' }}>
                              {isWithinExemptionPeriod(request.process_id) && (
                                <button
                                  onClick={() => handleSendNewDocument(request)}
                                  style={{
                                    ...styles.button,
                                    backgroundColor: '#3B82F6',
                                    color: 'white',
                                    fontSize: '11px',
                                    padding: '3px 6px',
                                    border: 'none',
                                    borderRadius: '4px'
                                  }}
                                  title="Permitir envio de novo documento"
                                >
                                  📄 Novo Doc
                                </button>
                              )}
                              {isWithinAppealPeriod(request.process_id) && (
                                <button
                                  onClick={() => handleAppeal(request)}
                                  style={{
                                    ...styles.button,
                                    backgroundColor: '#8B5CF6',
                                    color: 'white',
                                    fontSize: '11px',
                                    padding: '3px 6px',
                                    border: 'none',
                                    borderRadius: '4px'
                                  }}
                                  title="Interpor recurso"
                                >
                                  ⚖️ Recurso
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Devolver tarefa */}
      <Modal
        isOpen={showReturnModal}
        onClose={() => { setShowReturnModal(false); setRequestToReturn(null); setReturnReason(''); }}
        title="↩️ Devolver tarefa"
      >
        {requestToReturn && (
          <div>
            <p style={{ marginBottom: '16px', fontSize: '14px', color: '#6B7280' }}>
              Esta tarefa voltará para a fila de atribuição do admin. O motivo será exibido quando o admin for reatribuir.
            </p>
            <div style={{ marginBottom: '16px' }}>
              <label style={styles.label}>Motivo da devolução (opcional)</label>
              <textarea
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                style={styles.textarea}
                placeholder="Ex.: Documentos incompletos, necessidade de mais informações..."
                rows="4"
              />
            </div>
            <div style={{ ...styles.flex, ...styles.gap4, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowReturnModal(false); setRequestToReturn(null); setReturnReason(''); }}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                Cancelar
              </button>
              <button
                onClick={handleReturnTask}
                style={{ ...styles.button, ...styles.buttonPrimary }}
              >
                Devolver
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Análise de Isenção - SIMPLIFICADO */}
      <Modal
        isOpen={showAnalysisModal}
        onClose={() => setShowAnalysisModal(false)}
        title={`🎫 Análise de Isenção - ${selectedRequest?.candidate_name}`}
        large={false}
      >
        {selectedRequest && (
          <div>
            {/* Identificação do Candidato */}
            <div style={{ ...styles.alertInfo, marginBottom: '20px' }}>
              <div style={{ fontSize: '14px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>👤 Candidato:</strong> {selectedRequest.candidate_name}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>📄 CPF:</strong> {selectedRequest.candidate_cpf}
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <strong>NIS:</strong> {selectedRequest.exemption_nis || '—'}
                </div>
                <div>
                  <strong>🎓 Curso:</strong> {selectedRequest.course_name || courses.find(c => c.id === selectedRequest.course_id)?.name || 'N/A'}<br/>
                  <strong>📍 Unidade:</strong> {selectedRequest.course_campus || courses.find(c => c.id === selectedRequest.course_id)?.campus || '—'}
                </div>
              </div>
            </div>

            {/* Aviso de reenvio (só na fila ativa; em rejected o exemption_reason é o indeferimento atual) */}
            {selectedRequest.exemption_reason &&
              (selectedRequest.status === 'pending' || selectedRequest.status === 'analyzing') && (
              <div style={{
                marginBottom: '20px',
                padding: '12px',
                backgroundColor: '#FEF3C7',
                border: '1px solid #FCD34D',
                borderRadius: '8px',
                color: '#92400E',
                fontSize: '14px'
              }}>
                <strong>📄 Documento reenviado.</strong> Motivo do indeferimento anterior: {selectedRequest.exemption_reason}
              </div>
            )}


            {(selectedRequest.exemption_nis || selectedRequest.exemption_cadunico_consulta) && (
              <div style={{
                marginBottom: '20px',
                padding: '12px',
                backgroundColor: '#EFF6FF',
                border: '1px solid #BFDBFE',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <strong>Consulta CadÚnico</strong>
                  <button
                    type="button"
                    onClick={handleConsultCadunico}
                    disabled={cadunicoConsulting || !selectedRequest.exemption_nis}
                    style={{
                      ...styles.button,
                      ...styles.buttonSecondary,
                      fontSize: '12px',
                      padding: '6px 12px',
                      opacity: (cadunicoConsulting || !selectedRequest.exemption_nis) ? 0.7 : 1
                    }}
                  >
                    {cadunicoConsulting ? 'Consultando…' : 'Consultar CadÚnico'}
                  </button>
                </div>
                {!selectedRequest.exemption_nis && (
                  <div style={{ color: '#6B7280' }}>Candidato sem NIS informado.</div>
                )}
                {selectedRequest.exemption_cadunico_result && (
                  <div style={{ marginTop: '8px', lineHeight: 1.5 }}>
                    <div><strong>Fonte:</strong> {selectedRequest.exemption_cadunico_result.source || '—'}</div>
                    <div><strong>CPF no CadÚnico:</strong> {String(!!selectedRequest.exemption_cadunico_result.cpf_in_cadunico)}</div>
                    <div><strong>Baixa renda:</strong> {String(!!selectedRequest.exemption_cadunico_result.low_income_family)}</div>
                    <div><strong>Cadastro atualizado:</strong> {String(!!selectedRequest.exemption_cadunico_result.registration_updated)}</div>
                    <div><strong>Mensagem:</strong> {selectedRequest.exemption_cadunico_result.message || '—'}</div>
                    {selectedRequest.exemption_cadunico_consulted_at && (
                      <div><strong>Consultado em:</strong> {selectedRequest.exemption_cadunico_consulted_at}</div>
                    )}
                    <div style={{ marginTop: '8px', color: '#1E40AF', fontSize: '13px' }}>
                      A consulta não aprova automaticamente. Deferir ou indeferir manualmente.
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Documento para Download */}
            <div style={{ marginBottom: '20px' }}>
              <label style={styles.label}>📁 Documento Comprobatório</label>
              <div style={{
                backgroundColor: '#F9FAFB',
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #E5E7EB',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '14px' }}>
                  {selectedRequest.documents?.[0]?.name || 'documento_isencao.pdf'}
                </span>
                <button
                  onClick={() => handleDownloadDocuments(selectedRequest)}
                  style={{
                    ...styles.button,
                    ...styles.buttonSecondary,
                    fontSize: '12px',
                    padding: '6px 12px'
                  }}
                >
                  📄 Download
                </button>
              </div>
            </div>

            {/* Decisão */}
            <div style={{ marginBottom: '20px' }}>
              <label style={styles.label}>⚖️ Situação *</label>
              <select
                value={analysisForm.decision}
                onChange={(e) => setAnalysisForm({ ...analysisForm, decision: e.target.value })}
                style={styles.select}
              >
                <option value="">Selecione...</option>
                <option value="approved">Deferido</option>
                <option value="rejected">Indeferido</option>
              </select>
            </div>

            {/* Motivo do Indeferimento (apenas se rejeitado) */}
            {analysisForm.decision === 'rejected' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={styles.label}>❌ Motivo do Indeferimento *</label>
                <select
                  value={analysisForm.rejection_reason}
                  onChange={(e) => setAnalysisForm({...analysisForm, rejection_reason: e.target.value})}
                  style={styles.select}
                >
                  <option value="">Selecione um motivo</option>
                  {rejectionReasons.filter(reason => reason.is_active).map(reason => (
                    <option key={reason.id} value={reason.title}>
                      {reason.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Observações Gerais */}
            <div style={{ marginBottom: '24px' }}>
              <label style={styles.label}>📝 Observações Gerais</label>
              <textarea
                value={analysisForm.notes}
                onChange={(e) => setAnalysisForm({...analysisForm, notes: e.target.value})}
                style={styles.textarea}
                placeholder="Observações sobre a análise do pedido de isenção..."
                rows="3"
              />
            </div>

            {/* Ações */}
            <div style={{ ...styles.flex, justifyContent: 'flex-end', ...styles.gap4 }}>
              <button
                onClick={() => setShowAnalysisModal(false)}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                ❌ Cancelar
              </button>
              <button
                onClick={saveExemptionAnalysis}
                disabled={
                  (analysisForm.decision !== 'approved' && analysisForm.decision !== 'rejected') ||
                  (analysisForm.decision === 'rejected' && !analysisForm.rejection_reason)
                }
                style={{
                  ...styles.button,
                  ...((analysisForm.decision !== 'approved' && analysisForm.decision !== 'rejected') ||
                      (analysisForm.decision === 'rejected' && !analysisForm.rejection_reason)
                    ? styles.buttonDisabled
                    : styles.buttonPrimary)
                }}
              >
                💾 Finalizar Análise
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
});

export default ExemptionAnalysis;
